import type { ClaseDeSueloPropuesta, FuenteDeLaPropuesta } from "@/lib/rellenoConIa";
import { textoDeDecimal } from "@/lib/montos";

import type { FilaDeSuelo } from "./TablaDeClasesDeSuelo";

/* ---------------------------------------------------------------------------
   "Rellenar con IA" en la "Descripción de suelos": mezclar lo que el modelo
   propuso con lo que ya hay en el formulario.

   VIVE FUERA DEL COMPONENTE por lo mismo que su gemelo de aguas: es lo único
   de la entrega que puede estar mal sin que nada se vea roto. Un merge que pise
   borra trabajo sin un error en consola, y uno que duplique al segundo clic
   duplica el SUBTOTAL del cuadro — la cifra que se compara contra la superficie
   declarada del predio.

   ══ Y ACÁ HAY UNA REGLA QUE AGUAS NO TENÍA ═══════════════════════════════════

   Aguas solo traía LISTAS, y una lista se agrega al final sin tocar nada. Esta
   pestaña trae además dos CAMPOS DE TEXTO, y un texto no se agrega: se pisa.
   Peor todavía, esos dos textos llevan casilla "Información validada", así que
   sobrescribir uno ya revisado dejaría marcado como validado algo que nadie
   miró. De ahí la regla: los textos se escriben SOLO SI ESTÁN VACÍOS.
--------------------------------------------------------------------------- */

interface BorradorDeSuelos {
  filas: FilaDeSuelo[];
  topografia: string;
  descripcionSuelos: string;
}

interface PropuestaDeSuelos {
  clases: ClaseDeSueloPropuesta[];
  topografia: string | null;
  topografia_fuente?: FuenteDeLaPropuesta | null;
  descripcion_general: string | null;
  descripcion_general_fuente?: FuenteDeLaPropuesta | null;
}

export interface MezclaDeSuelos extends BorradorDeSuelos {
  /** Cuántas filas se AGREGARON de verdad, ya descontadas las repetidas. */
  clasesAgregadas: number;
  topografiaEscrita: boolean;
  descripcionEscrita: boolean;
  /**
   * De qué documento dice la IA que salió cada fila NUEVA, por su `clave`.
   * Va aparte de la fila porque la fila es lo que se guarda y la fuente no.
   */
  fuentesPorClave: Record<string, FuenteDeLaPropuesta | null>;
  /**
   * La fuente de cada texto, SOLO si la IA lo escribió. `undefined` es "no hay
   * nada que citar" (el campo ya tenía texto); `null`, "no indicada".
   */
  topografiaFuente: FuenteDeLaPropuesta | null | undefined;
  descripcionFuente: FuenteDeLaPropuesta | null | undefined;
}

/**
 * Agrega lo propuesto a lo que ya hay, sin pisar nada de lo anterior.
 *
 * LAS FILAS SE AGREGAN AL FINAL y las que ya estaban se quedan tal cual: quitar
 * una de más es un clic, y recuperar la que el botón se llevó no se puede.
 *
 * NO SE REPITE LO QUE YA ESTÁ. El modelo corre a temperatura cero, así que dos
 * clics sobre el mismo expediente devuelven lo mismo. La comparación es contra
 * lo que YA ESTABA en el formulario, nunca dentro de la propia propuesta: el
 * avalúo del SII lista "Clase III riego" y "Clase III secano" por separado, y
 * colapsarlas perdería una superficie.
 *
 * LOS TEXTOS SOLO SE ESCRIBEN SI ESTÁN VACÍOS. Ver el encabezado del módulo.
 */
export function sumarLaPropuestaDeSuelos(
  actual: BorradorDeSuelos,
  propuesta: PropuestaDeSuelos,
): MezclaDeSuelos {
  const previas = new Set(actual.filas.map(huellaDeFila));

  const fuentesPorClave: MezclaDeSuelos["fuentesPorClave"] = {};

  const nuevas = propuesta.clases.flatMap((clase) => {
    const fila = filaDesdeLaClasePropuesta(clase);
    if (previas.has(huellaDeFila(fila))) return [];

    fuentesPorClave[fila.clave] = clase.fuente ?? null;
    return [fila];
  });

  const topografia = propuesta.topografia?.trim() ?? "";
  const descripcion = propuesta.descripcion_general?.trim() ?? "";

  const escribeTopografia = estaVacio(actual.topografia) && topografia !== "";
  const escribeDescripcion = estaVacio(actual.descripcionSuelos) && descripcion !== "";

  return {
    filas: [...actual.filas, ...nuevas],
    topografia: escribeTopografia ? topografia : actual.topografia,
    descripcionSuelos: escribeDescripcion ? descripcion : actual.descripcionSuelos,
    clasesAgregadas: nuevas.length,
    topografiaEscrita: escribeTopografia,
    descripcionEscrita: escribeDescripcion,
    fuentesPorClave,
    topografiaFuente: escribeTopografia ? (propuesta.topografia_fuente ?? null) : undefined,
    descripcionFuente: escribeDescripcion
      ? (propuesta.descripcion_general_fuente ?? null)
      : undefined,
  };
}

/**
 * Qué decir después del clic.
 *
 * CUENTA LO QUE SE AGREGÓ, NO LO QUE VINO. Si la propuesta traía tres clases que
 * ya estaban y una topografía que no se escribió porque el campo tenía texto,
 * decir "3 clases y la topografía" afirmaría un cambio en una pantalla donde no
 * cambió nada — y quien lo lea se va a poner a buscarlo.
 */
export function resumenDeLoPropuestoEnSuelos(
  clases: number,
  topografia: boolean,
  descripcion: boolean,
): string {
  const partes: string[] = [];

  if (clases > 0) {
    partes.push(`${clases} ${clases === 1 ? "clase" : "clases"} de uso de suelo`);
  }

  if (topografia) partes.push("la topografía");
  if (descripcion) partes.push("la descripción de los suelos");

  if (partes.length === 0) {
    return "No encontramos nada nuevo que agregar en los documentos leídos.";
  }

  // El verbo concuerda con el número de CLASES, que es lo único contable: "Se
  // agregó la topografía" y "Se agregaron 3 clases" son las dos formas
  // correctas, y con una sola clase más un texto manda el plural.
  const cosas = clases + (topografia ? 1 : 0) + (descripcion ? 1 : 0);
  const verbo = cosas === 1 && clases <= 1 ? "Se agregó" : "Se agregaron";

  return `${verbo} ${enumerar(partes)}.`;
}

/**
 * "a, b y c" — la coma del español, con "y" antes del último.
 *
 * Va acá y no con `join(", ")` porque esta frase la lee una persona: "3 clases,
 * la topografía, la descripción" se lee como una lista de sistema y no como una
 * oración.
 */
function enumerar(partes: string[]): string {
  if (partes.length <= 1) return partes[0] ?? "";

  return `${partes.slice(0, -1).join(", ")} y ${partes[partes.length - 1]}`;
}

/**
 * Un campo de texto que se puede rellenar sin pisar nada.
 *
 * SE RECORTA antes de decidir: un campo con espacios es un campo vacío para
 * quien lo mira, y tratarlo como escrito dejaría la propuesta afuera por algo
 * que no se ve.
 */
function estaVacio(valor: string): boolean {
  return valor.trim() === "";
}

/**
 * Una clase propuesta, con la forma que tiene una fila del cuadro.
 *
 * LA SUPERFICIE PASA POR `textoDeDecimal`, que pone la COMA decimal: con el
 * punto, 118.5 volvería al servidor como ciento dieciocho mil quinientos —
 * `numeroEscrito` lee el punto como separador de miles, que es lo que es en
 * este formulario.
 *
 * `validada` SIEMPRE EN FALSO: esa casilla dice que una persona revisó lo que
 * la IA propuso.
 */
function filaDesdeLaClasePropuesta(clase: ClaseDeSueloPropuesta): FilaDeSuelo {
  return {
    clave: crypto.randomUUID(),
    sueloClaseId: clase.suelo_clase_id,
    descripcion: clase.descripcion ?? "",
    superficie: clase.superficie === null ? "" : textoDeDecimal(String(clase.superficie)),
    validada: false,
  };
}

/**
 * Qué hace a dos filas "la misma" para no repetirlas.
 *
 * SE MIRAN LOS TRES CAMPOS DEL DATO y ninguno de los otros: ni la `clave` —que
 * es distinta por construcción— ni `validada`, que dice quién revisó la fila y
 * no qué dice. Con `validada` adentro, volver a apretar el botón después de
 * validar una clase agregaría su duplicado sin validar.
 */
function huellaDeFila(fila: FilaDeSuelo): string {
  return [fila.sueloClaseId ?? "", fila.descripcion.trim(), fila.superficie.trim()].join(" ");
}
